import { Image } from '@fluentui/react/lib/Image';
import { ITheme } from '@fluentui/style-utilities';
import { getOwaTheme } from 'owa-color-utils/lib/colorUtils';
import { ThemeProvider } from '@fluentui/react/lib/utilities/ThemeProvider';
import type { IFluidCodeDetails, IFluidObject } from '@fluidframework/core-interfaces';
import {
    ActionMenu,
    BackplateComponentSpace,
    BackplateComponentSpaceStyles,
    ComponentPartStyles,
    ComponentUXWrapper,
    ComponentUXWrapperStyles,
    LeapPart,
    MessageBar,
    LoadingPlaceholder,
    ComponentPart,
    Chrome,
    FluidIcon,
    ChromeStyles,
} from '@fluidx/host-ux';
import {
    determineLayoutMode,
    ComponentLoaderData,
    ExternalContainer,
    LayoutMode,
    MessageBannerProvider,
} from '@fluidx/office-fluid-types';
import { Locale, resolveLocale } from '@fluidx/utilities';
import {
    DriverInfo,
    createComponentFromCodeInIframe,
    createComponentFromCodeInSourcelessIframe,
    getFullODSPFileName,
    loadComponentInIframe,
    loadComponentInSourcelessIframe,
    CreateFromCodeInIframeProps,
    LoadInIframeResult,
    OfficeWebHostScope,
    SourcelessIframeLoadOptions,
} from '@ms/office-web-host';
import { observer } from 'mobx-react-lite';
import { assertNever } from 'owa-assert';
import { getScriptPath } from 'owa-config';
import { getOwaResourceUrl } from 'owa-resource-url';
import { isFeatureEnabled } from 'owa-feature-flags';
import { FluidOwaSource, isCalendarSource } from 'owa-fluid-validations';
import loc, { getCurrentLanguage } from 'owa-localize';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import type { FluidRenderProps } from '../store/schema/FluidRenderProps';
import { reportPerfCheckmark, OWAFluidCheckmarksEnum } from 'owa-fluid-perf-datapoints';
import { logUsage } from 'owa-analytics';
import { onFluidComponentRendered } from '../actions/publicActions';
import styles from './FluidComponent.scss';
import { getCopyLinkMenuItem } from '../utils/fluidProps/getActionMenuItems';
import { iframeTitleText } from './FluidComponent.locstring.json';

interface FluidComponentProps {
    onIFrameLoaded: (any: any) => void;
    fluidRenderProps: FluidRenderProps;
    scope: OfficeWebHostScope;
    driverInfo: DriverInfo;
    headerNode: Node;
    layoutMode: LayoutMode;
    fluidOwaSource: FluidOwaSource;
    controlNode: React.ReactElement<{}> | undefined;
    isCreateNew: boolean;
    heroComponentConfiguration: ComponentLoaderData | undefined;
    autoFocus: boolean | undefined;
    viewStateId: string | undefined;
    containerId: string | undefined;
    fromExistingTemplateUrl: string | undefined;
}

import ONENOTE_ICON from './icons/OneNote.png';
const MAX_IFRAME_LOAD_TIME = 60000;

const FluidComponent = observer(function FluidComponent(props: FluidComponentProps) {
    const theme = getOwaTheme();
    const locale = getCurrentLanguage();
    const fluidLocale: Locale = resolveLocale(locale);
    const hideActionMenu = props.fluidOwaSource === FluidOwaSource.Timestream;
    const useSourceless =
        isFeatureEnabled('cmp-fluid-sourcelessIFrameAlways') ||
        isMemoryLeakFixed(props.fluidOwaSource);
    let iFrameLoadTimer;

    const componentUxWrapperRef = React.createRef<HTMLDivElement>();
    const [loadedContainerAndComponent, setLoadedContainerAndComponent] = React.useState<
        | {
              container: ExternalContainer;
              component: IFluidObject;
          }
        | undefined
    >();
    const [hostingDiv, setHostingDiv] = React.useState<HTMLDivElement>();
    const [bannerProviders, setBannerProviders] = React.useState<MessageBannerProvider[]>([]);

    const errorActionHandlers = <ActionType extends 'Reload' | 'Upgrade' | 'ViewFileLocation'>(
        _actionType: ActionType,
        _errorType: any
    ) => {
        setLoadedContainerAndComponent(undefined);

        // We clear the existing instance with the error before reloading the new instance of the component
        if (hostingDiv) {
            hostingDiv.innerText = '';
            loadComponentCallback(hostingDiv);
        }
        return;
    };

    const returnHostingDiv = React.useCallback((div: HTMLDivElement) => {
        setHostingDiv(div);
    }, []);

    const title = loc(iframeTitleText);

    async function displayComponent(renderTarget: HTMLElement) {
        let result: LoadInIframeResult;
        reportPerfCheckmark(OWAFluidCheckmarksEnum.iframeBootStarted, props.fluidOwaSource);
        if (useSourceless) {
            const options: SourcelessIframeLoadOptions = {
                sourcelessIframeBundleUrl: getScriptPath() + 'owa.officeWebHost.js',
                useServiceDeliveredDriver: true,
                iframeTitle: title,
            };

            // Hero component configuration is provided for creating new fluid components.
            if (props.isCreateNew) {
                result = createComponentFromCodeInSourcelessIframe(
                    {
                        driverType: props.driverInfo.type,
                        componentDiv: renderTarget,
                        scope: props.scope,
                        codeDetails: props.fluidRenderProps.hostConfig.codeDetails,
                        config: props.fluidRenderProps.hostConfig,
                        heroComponentConfiguration: props.heroComponentConfiguration,
                        fromExistingTemplateUrl: props.fromExistingTemplateUrl,
                    },
                    options
                );
            } else {
                result = loadComponentInSourcelessIframe(
                    props.driverInfo,
                    renderTarget,
                    props.scope,
                    props.fluidRenderProps.hostConfig,
                    options
                );
            }
        } else {
            if (props.isCreateNew) {
                const createFromCodeProps: CreateFromCodeInIframeProps = {
                    driverType: props.driverInfo.type,
                    componentDiv: renderTarget,
                    scope: props.scope,
                    codeDetails: props.fluidRenderProps.hostConfig.codeDetails,
                    config: props.fluidRenderProps.hostConfig,
                    heroComponentConfiguration: props.heroComponentConfiguration,
                    fromExistingTemplateUrl: props.fromExistingTemplateUrl,
                };
                result = createComponentFromCodeInIframe(createFromCodeProps, {
                    iframeLoadTimeout: 0,
                    iframeTitle: title,
                });
            } else {
                result = loadComponentInIframe(
                    props.driverInfo,
                    renderTarget,
                    props.scope,
                    props.fluidRenderProps.hostConfig,
                    {
                        iframeLoadTimeout: 0,
                        iframeTitle: title,
                    }
                );
            }
        }

        props.onIFrameLoaded(result);
        return result.loadedInfo;
    }

    async function loadComponentAsync(hostingDiv: HTMLDivElement) {
        try {
            const loadedInfo = await displayComponent(hostingDiv);
            const [container, component] = await Promise.all([
                loadedInfo.container,
                loadedInfo.component,
            ]);
            if (container && component) {
                const canvasComponent: IFluidObject | undefined = useSourceless
                    ? await component.ComponentCanvasProvider?.getCanvasComponent()
                    : undefined;

                component.KeyboardShortcutEmitter?.setShortcuts([
                    {
                        keyboardShortcutDescription: {
                            type: 'keydown',
                            key: 'Escape',
                        },
                        handler: () => hostingDiv.focus(),
                    },
                ]);
                setLoadedContainerAndComponent({
                    container,
                    component: canvasComponent ?? component,
                });

                const componentBannerProvider: MessageBannerProvider | undefined =
                    component.MessageBannerProvider;
                setBannerProviders([
                    ...bannerProviders,
                    ...(componentBannerProvider ? [componentBannerProvider] : []),
                ]);
            }
        } catch (e) {
            // Ignore any errors thrown here since they are being caught by the consumers of this file
        }
    }

    const loadComponentCallback = React.useCallback((hostingDiv: HTMLDivElement) => {
        // To display the component within the ux, we first need the Ux to render to
        // create an element we can give to the component.
        if (hostingDiv && !loadedContainerAndComponent) {
            loadComponentAsync(hostingDiv);
            clearTimeout(iFrameLoadTimer);
            iFrameLoadTimer = setTimeout(() => {
                if (!loadedContainerAndComponent) {
                    logUsage('ContainerAndComponentLoadTimeout', undefined, { isCore: true });
                }
            }, MAX_IFRAME_LOAD_TIME);
        }
    }, []);

    React.useEffect(() => {
        if (!loadedContainerAndComponent && hostingDiv) {
            loadComponentCallback(hostingDiv);
        }
        if (props.autoFocus && componentUxWrapperRef.current) {
            componentUxWrapperRef.current.focus();
        }
    }, [hostingDiv]);

    React.useEffect(() => {
        if (loadedContainerAndComponent) {
            if (props.autoFocus) {
                if (useSourceless) {
                    loadedContainerAndComponent.component.ComponentFocusable?.giveFocus();
                } else {
                    loadedContainerAndComponent.component.ComponentCanvasProvider?.getCanvasComponent?.().then(
                        canvasComponent => canvasComponent?.ComponentFocusable?.giveFocus()
                    );
                }
            }
            reportPerfCheckmark(
                OWAFluidCheckmarksEnum.templateRenderComplete,
                props.fluidOwaSource
            );
        }
    }, [loadedContainerAndComponent]);

    React.useEffect(() => {
        return () => {
            clearTimeout(iFrameLoadTimer);
            setLoadedContainerAndComponent(undefined);
            setHostingDiv(undefined);
        };
    }, []);

    React.useEffect(() => {
        onFluidComponentRendered(props.fluidOwaSource, props.isCreateNew, props.viewStateId);
    }, []);

    const componentPartStyle: ComponentPartStyles = {
        root: loadedContainerAndComponent
            ? {
                  // This padding has been added to clearly show the focus ring
                  // around the fluid component content.
                  padding: '1px',
              }
            : {
                  // Hide the component Part while displaying the placeholder
                  maxHeight: 0,
                  maxWidth: props.layoutMode === LayoutMode.grow ? 0 : undefined,
                  overflow: 'hidden',
              },
    };

    const componentUXWrapperStyle: ComponentUXWrapperStyles = {
        root: {
            padding: '0',
            width: '100%',
            borderRadius: '4px',
            borderColor: getBorderColor(theme, props.fluidOwaSource),
            borderWidth: '1px',
            borderStyle: 'solid',
            backgroundColor: 'transparent',
            selectors: {
                ':focus': {
                    borderColor: theme.palette.neutralPrimary,
                },
                ':hover': {
                    borderColor: theme.palette.neutralPrimary,
                },
            },
        },
    };

    const backplateComponentSpaceStyle: BackplateComponentSpaceStyles = {
        root: {
            margin: '0',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            overflow: 'hidden', // allows the border radius to clip the contents
            boxShadow: '',
            borderRadius: '0',
            borderTop: '1px solid ' + theme.palette.neutralLight,
            backgroundColor: 'transparent',
            selectors: {
                ':hover': {
                    boxShadow: '',
                },
            },
        },
    };

    const chromeStyle: ChromeStyles = {
        root: {
            margin: '0',
            padding: '0',
            position: 'relative',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: '48px',
            maxHeight: '48px',
            paddingLeft: '8px',
            paddingRight: '8px',
            fontSize: '12px',
            lineHeight: '20px',
            flexGrow: 1,
            overflow: 'hidden', // allows the border radius to clip the contents
            borderRadius: '0px',
            backgroundColor: 'transparent',
        },
    };

    const getActionMenuItems = () => {
        const menuItems = props.fluidRenderProps.menuItems;
        if (props.isCreateNew && props.containerId) {
            return [
                getCopyLinkMenuItem(props.isCreateNew, undefined /* url */, props.containerId),
                ...menuItems,
            ];
        }
        return menuItems;
    };

    return (
        <>
            <ThemeProvider theme={theme}>
                {/* uncomment to test error states*/
                /* loadedContainerAndComponent && <ContainerStateWidget container={loadedContainerAndComponent.container} />*/}
                <ComponentUXWrapper
                    wrapperRef={componentUxWrapperRef}
                    localization={{ locale: fluidLocale }}
                    layoutMode={props.layoutMode}
                    {...loadedContainerAndComponent}
                    standardRootBorderWidthPx={
                        isCalendarSource(props.fluidOwaSource) ? 1 : undefined
                    }
                    styles={
                        isCalendarSource(props.fluidOwaSource) ? componentUXWrapperStyle : undefined
                    }>
                    <Chrome
                        primarySpace={
                            <>
                                {renderEditorIcon(
                                    props.fluidOwaSource,
                                    props.fluidRenderProps.hostConfig.codeDetails,
                                    props.driverInfo.containerCodeHint,
                                    props.driverInfo
                                )}
                                <Header headerNode={props.headerNode} />
                            </>
                        }
                        secondarySpace={
                            <>
                                <LeapPart
                                    tokenProvider={props.fluidRenderProps.scopeProps.tokenProvider}
                                    presenceColorProvider={
                                        props.fluidRenderProps.scopeProps.presenceColorProvider
                                    }
                                />

                                {!hideActionMenu && (
                                    <ActionMenu
                                        hidePreviewItem={true}
                                        menuItems={getActionMenuItems()}
                                        styles={{
                                            subComponentStyles: {
                                                overflowButton: {
                                                    root: { height: '32px', width: '32px' },
                                                },
                                            },
                                        }}
                                    />
                                )}
                                {props.controlNode}
                            </>
                        }
                        styles={isCalendarSource(props.fluidOwaSource) ? chromeStyle : {}}
                    />
                    <BackplateComponentSpace
                        styles={
                            isCalendarSource(props.fluidOwaSource)
                                ? backplateComponentSpaceStyle
                                : undefined
                        }>
                        <MessageBar
                            containerBannerActionHandlers={errorActionHandlers}
                            bannerProviders={bannerProviders}
                        />
                        {!loadedContainerAndComponent && <LoadingPlaceholder />}
                        <ComponentPart
                            returnHostingDiv={returnHostingDiv}
                            layoutMode={props.layoutMode}
                            style={componentPartStyle}
                        />
                    </BackplateComponentSpace>
                </ComponentUXWrapper>
            </ThemeProvider>

            {props.fluidRenderProps.scopeProps.commandingSurfaceParts && hostingDiv && (
                <props.fluidRenderProps.scopeProps.commandingSurfaceParts.commandingSurfaceUX
                    viewProvider={
                        props.fluidRenderProps.scopeProps.commandingSurfaceParts.viewProvider
                    }
                    fluidHostingElementId={hostingDiv.id}
                />
            )}
        </>
    );
});

// VSO 95689 This function exists to convert the Node to an Element, so we can insert
// it into the DOM. This seems like overkill, look up an easier way.
const Header = observer(function Header(props: { headerNode: Node }) {
    const headerElement = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (headerElement.current) {
            headerElement.current.replaceWith(props.headerNode);
        }
    }, []);

    return <div className={styles.title} ref={headerElement} />;
});

export function renderFluidComponent(
    fluidRenderProps: FluidRenderProps,
    scope: OfficeWebHostScope,
    targetDiv: HTMLDivElement,
    driverInfo: DriverInfo,
    headerNode: Node,
    resolve: (any: any) => void,
    fluidOwaSource: FluidOwaSource,
    isCreateNew: boolean,
    controlNode?: React.ReactElement<{}>,
    heroComponentConfiguration?: ComponentLoaderData, // should only be defined for create new from discover menu
    autoFocus?: boolean,
    viewStateId?: string,
    containerId?: string,
    fromExistingTemplateUrl?: string
) {
    // Layout mode should be determined before the component is rendered
    const layoutMode = determineLayoutMode(targetDiv);

    ReactDOM.render(
        <React.StrictMode>
            <FluidComponent
                onIFrameLoaded={resolve}
                fluidRenderProps={fluidRenderProps}
                scope={scope}
                driverInfo={driverInfo}
                headerNode={headerNode}
                layoutMode={layoutMode}
                fluidOwaSource={fluidOwaSource}
                controlNode={controlNode}
                isCreateNew={isCreateNew}
                heroComponentConfiguration={heroComponentConfiguration}
                autoFocus={autoFocus}
                viewStateId={viewStateId}
                containerId={containerId}
                fromExistingTemplateUrl={fromExistingTemplateUrl}
            />
        </React.StrictMode>,
        targetDiv
    );
}

function renderEditorIcon(
    fluidOwaSource: FluidOwaSource,
    codeDetails: IFluidCodeDetails | undefined,
    containerCodeHint: string | undefined,
    driverInfo: DriverInfo
): JSX.Element {
    // We generate a file name on an empty string to check the extension to see if the fluid file is OneNote hosted or not.
    const fileExtension = codeDetails && getFullODSPFileName(codeDetails, '' /* fileName */);
    if (
        isCalendarSource(fluidOwaSource) ||
        fileExtension === '.note' ||
        containerCodeHint === '@ms/onenote-meetings' ||
        driverInfo.containerCodeHint === '@ms/onenote-meetings'
    ) {
        return (
            <div className={styles.image}>
                <Image
                    title={'OneNote'}
                    shouldFadeIn={false}
                    width={20}
                    src={getOwaResourceUrl(ONENOTE_ICON)}
                />
            </div>
        );
    } else {
        return (
            <FluidIcon
                styles={{
                    root: {
                        fill: '#0078D4',
                        marginRight: '5px',
                    },
                }}
            />
        );
    }
}

// Sourceless iframes leak memory in editors. Memory leak fixed indicates that it is a
// scenario where the memory leak has been fixed (typically by not using an editor)
// so it is safe to use sourceless iframes. This property will be removed once the leak
// is fixed everywhere. VSO 104332
// Note - timestream has not been tested for a memory leak
function isMemoryLeakFixed(fluidOwaSource: FluidOwaSource): boolean {
    switch (fluidOwaSource) {
        case FluidOwaSource.CalendarCompose:
        case FluidOwaSource.AgendaEditor:
            return false;

        case FluidOwaSource.MailCalendarCard:
        case FluidOwaSource.CalendarReadingPane:
        case FluidOwaSource.Timestream:
        case FluidOwaSource.MailReadingPane:
        case FluidOwaSource.MailCompose:
        case FluidOwaSource.None:
            return true;
        default:
            assertNever(fluidOwaSource);
    }
}

function getBorderColor(theme: ITheme, fluidOwaSource: FluidOwaSource): string {
    switch (fluidOwaSource) {
        case FluidOwaSource.CalendarReadingPane:
        case FluidOwaSource.MailCalendarCard:
            return theme.palette.neutralTertiaryAlt;
        default:
            return theme.palette.neutralTertiary;
    }
}
